import { ref, unref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFetchAction } from '@hooks/fetch';
import type LogicGroup from '@utils/logicGate/LogicGroup.vue';
import type { MaybeRef } from '@vueuse/core';
import type { Filter, LogicStatement, MaybeHydra } from '@/entities/Entities';

// eslint-disable-next-line import/prefer-default-export
export const useCheckFilter = (
  route: string,
  filter: MaybeRef<Filter>,
) => {
  const checkedNumberArray = ref<Record<string, string>[] | undefined>(undefined);
  const checkedNumber = ref<string|undefined>(undefined);

  const error = ref<string|undefined>(undefined);

  const { t } = useI18n();

  const [exec, { loading }] = useFetchAction();

  const stripIdsFromFilter = (fil: MaybeHydra<Filter>): MaybeHydra<Filter> | null => {
    const newFilter = JSON.parse(JSON.stringify(fil));
    newFilter.logicGroups = fil.logicGroups.map(
      (group: MaybeHydra<LogicGroup>): MaybeHydra<LogicGroup> => ({
        id: undefined,
        logicStatements: group.logicStatements.map(
          (statement: MaybeHydra<LogicStatement>): MaybeHydra<LogicStatement> => ({
            id: undefined,
            name: statement.name,
            data: statement.data,
          }),
        ),
      }),
    );
    if (newFilter.logicGroups.length === 0) {
      return null;
    }
    newFilter.id = undefined;
    newFilter['@id'] = undefined;
    return newFilter;
  };

  const checkFilter = async (
    validationFunction?: ((data: MaybeHydra<Filter>) => boolean) | undefined,
    additionalPayload?: object,
  ) => {
    error.value = undefined;
    checkedNumber.value = undefined;
    checkedNumberArray.value = undefined;

    if (!validationFunction?.(unref(filter))) {
      error.value = t('filter.invalid');
      return;
    }
    const answer = await exec<{
      number?: string;
      numberArray?: Record<string, string>[];
    }>(`${route}/check`, {
      method: 'POST',
      body: { ...stripIdsFromFilter(unref(filter)), ...additionalPayload },
    });
    checkedNumber.value = answer.number;
    checkedNumberArray.value = answer.numberArray;
  };

  watch(() => unref(filter), () => {
    checkedNumber.value = undefined;
    checkedNumberArray.value = undefined;
    error.value = undefined;
  }, { deep: true });

  return {
    checkFilter,
    loading,
    checkedNumber,
    checkedNumberArray,
    error,
  };
};
